<!-- app/javascript/dashboard/routes/dashboard/bulkmessage/components/modals/RetryBroadcastModal.vue -->
<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  broadcast: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close', 'proceed']);

// State
const selectedTypes = ref([]); // Will be set based on available counts

// Parse recipient data from broadcast
const totalRecipients = computed(() => {
  if (!props.broadcast || !props.broadcast.total_recipients) return [];
  try {
    const data = JSON.parse(props.broadcast.total_recipients);
    return Array.isArray(data) ? data : [];
  } catch (e) {
    console.error('Error parsing total recipients:', e);
    return [];
  }
});

const successfulPhones = computed(() => {
  if (!props.broadcast || !props.broadcast.successful_phones) return [];
  try {
    const data = JSON.parse(props.broadcast.successful_phones);
    return Array.isArray(data) ? data : [];
  } catch (e) {
    console.error('Error parsing successful phones:', e);
    return [];
  }
});

const failedPhones = computed(() => {
  if (!props.broadcast || !props.broadcast.failed_phones) return [];
  try {
    const data = JSON.parse(props.broadcast.failed_phones);
    return Array.isArray(data) ? data : [];
  } catch (e) {
    console.error('Error parsing failed phones:', e);
    return [];
  }
});

// Calculate counts
const totalCount = computed(() => totalRecipients.value.length);
const successCount = computed(() => successfulPhones.value.length);
const failureCount = computed(() => failedPhones.value.length);
const pendingCount = computed(() => {
  return totalCount.value - (successCount.value + failureCount.value);
});

// Calculate selected recipients based on selected types
const selectedRecipients = computed(() => {
  // Create maps for quick lookup with inbox_id
  const successfulPhonesMap = new Map(
    successfulPhones.value.map(p => [p.phone, p])
  );
  const failedPhonesMap = new Map(failedPhones.value.map(p => [p.phone, p]));

  let recipients = [];

  // Add successful recipients if selected
  if (selectedTypes.value.includes('success')) {
    const successfulRecipients = totalRecipients.value
      .filter(recipient => {
        const phone = recipient.phone || recipient.phone_number;
        return successfulPhonesMap.has(phone);
      })
      .map(recipient => {
        const phone = recipient.phone || recipient.phone_number;
        const successData = successfulPhonesMap.get(phone);
        return {
          ...recipient,
          inbox_id: successData?.inbox_id || recipient.inbox_id,
        };
      });
    recipients = [...recipients, ...successfulRecipients];
  }

  // Add failed recipients if selected
  if (selectedTypes.value.includes('failed')) {
    const failedRecipients = totalRecipients.value
      .filter(recipient => {
        const phone = recipient.phone || recipient.phone_number;
        return failedPhonesMap.has(phone);
      })
      .map(recipient => {
        const phone = recipient.phone || recipient.phone_number;
        const failedData = failedPhonesMap.get(phone);
        return {
          ...recipient,
          inbox_id: failedData?.inbox_id || recipient.inbox_id,
        };
      });
    recipients = [...recipients, ...failedRecipients];
  }

  // Add pending recipients if selected
  if (selectedTypes.value.includes('pending')) {
    const pendingRecipients = totalRecipients.value.filter(recipient => {
      const phone = recipient.phone || recipient.phone_number;
      return !successfulPhonesMap.has(phone) && !failedPhonesMap.has(phone);
    });
    recipients = [...recipients, ...pendingRecipients];
  }

  // Remove duplicates (in case of any)
  const uniqueRecipients = recipients.filter(
    (recipient, index, self) =>
      index ===
      self.findIndex(
        r =>
          (r.phone || r.phone_number) ===
          (recipient.phone || recipient.phone_number)
      )
  );

  return uniqueRecipients;
});

const selectedCount = computed(() => selectedRecipients.value.length);

// Set default selection based on available counts
const setDefaultSelection = () => {
  if (pendingCount.value > 0) {
    selectedTypes.value = ['pending'];
  } else if (successCount.value > 0) {
    selectedTypes.value = ['success'];
  } else {
    selectedTypes.value = ['failed'];
  }
};

// Reset state when modal is opened and set default selection
watch(
  () => props.show,
  newValue => {
    if (newValue) {
      setDefaultSelection();
    } else {
      selectedTypes.value = [];
    }
  }
);

// Watch for changes in counts to update default selection
watch([pendingCount, successCount, failureCount], () => {
  if (props.show && selectedTypes.value.length === 0) {
    setDefaultSelection();
  }
});

// Toggle selection for a type
const toggleSelection = type => {
  const index = selectedTypes.value.indexOf(type);
  if (index > -1) {
    selectedTypes.value.splice(index, 1);
  } else {
    // Add if not selected
    selectedTypes.value.push(type);
  }
};

// Select all types
const selectAll = () => {
  selectedTypes.value = ['success', 'failed', 'pending'];
};

// Calculate if all types are included (for backward compatibility)
const includeAll = computed(() => {
  return (
    selectedTypes.value.includes('success') &&
    selectedTypes.value.includes('failed') &&
    selectedTypes.value.includes('pending')
  );
});

// Handle proceed action
const handleProceed = () => {
  if (selectedCount.value === 0) return;

  // Debug: Log the selected recipients to see all available data
  console.log(
    'Selected recipients before processing (FULL DATA):',
    selectedRecipients.value
  );
  
  console.log(
    'Selected recipients before processing (SUMMARY):',
    selectedRecipients.value.map(r => ({
      name: r.name,
      phone: r.phone || r.phone_number,
      inbox_id: r.inbox_id,
      customFields: r.customFields,
      hasCustomFields: !!r.customFields,
      allKeys: Object.keys(r)
    }))
  );

  // Process recipients to preserve original phone numbers with country code, inbox_id and custom fields
  const processedRecipients = selectedRecipients.value.map(recipient => {
    const originalPhone = recipient.phone || recipient.phone_number;
    return {
      name: recipient.name || '',
      phone: originalPhone,
      phone_number: originalPhone,
      inbox_id: recipient.inbox_id || null, // Preserve the original inbox_id from the recipient
      customFields: recipient.customFields || {}, // Preserve custom fields for variable replacement
      // Preserve any other additional fields from CSV import
      ...Object.keys(recipient).reduce((acc, key) => {
        if (!['name', 'phone', 'phone_number', 'inbox_id', 'customFields'].includes(key)) {
          acc[key] = recipient[key];
        }
        return acc;
      }, {})
    };
  });

  console.log(
    'Processed recipients with original inbox_id and custom fields:',
    processedRecipients.map(r => ({
      name: r.name,
      phone: r.phone,
      inbox_id: r.inbox_id,
      customFields: r.customFields,
    }))
  );

  emit('proceed', {
    recipients: processedRecipients,
    broadcast: props.broadcast,
    includeAll: includeAll.value,
  });
};
</script>

<template>
  <transition name="modal-fade">
    <div
      v-if="show"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto"
    >
      <div
        class="bg-white dark:bg-n-slate-2 rounded-xl w-full max-w-3xl shadow-xl border border-n-slate-3 dark:border-n-slate-7 my-8"
      >
        <!-- Modal Header -->
        <div
          class="flex items-center justify-between p-6 border-b border-n-slate-3 dark:border-n-slate-7"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 bg-n-amber-2 dark:bg-n-amber-3/20 rounded-lg flex items-center justify-center"
            >
              <i
                class="i-lucide-repeat text-n-amber-9 dark:text-n-amber-10 text-xl"
              />
            </div>
            <h3
              class="text-lg font-semibold text-n-slate-12 dark:text-n-slate-12"
            >
              Reenviar Disparo
            </h3>
          </div>
          <button
            class="text-n-slate-9 hover:text-n-slate-11 dark:text-n-slate-10 dark:hover:text-n-slate-12 transition-colors"
            @click="$emit('close')"
          >
            <i class="i-lucide-x text-xl" />
          </button>
        </div>

        <!-- Modal Content -->
        <div class="p-6">
          <!-- Status Overview with Selectable Cards -->
          <div class="mb-6">
            <div class="flex items-center justify-between mb-3">
              <h4
                class="text-sm font-medium text-n-slate-11 dark:text-n-slate-10"
              >
                Selecione quem receberá o reenvio:
              </h4>
              <button
                class="text-xs px-3 py-1 rounded-lg bg-n-slate-2 hover:bg-n-slate-3 dark:bg-n-slate-4 dark:hover:bg-n-slate-5 text-n-slate-11 dark:text-n-slate-10 transition-colors"
                @click="selectAll"
              >
                <i class="i-lucide-check-square mr-1" />
                Selecionar todos
              </button>
            </div>
            <div class="grid grid-cols-3 gap-3">
              <!-- Success Card (Selectable) -->
              <div
                class="rounded-lg p-4 border-2 transition-all duration-200 cursor-pointer select-none"
                :class="
                  selectedTypes.includes('success')
                    ? 'bg-n-iris-1 dark:bg-n-iris-3/20 border-n-iris-9 dark:border-n-iris-8'
                    : 'bg-n-slate-1 dark:bg-n-slate-3 border-n-slate-3 dark:border-n-slate-6 hover:border-n-slate-5 dark:hover:border-n-slate-7'
                "
                @click="toggleSelection('success')"
              >
                <div class="flex items-center justify-between mb-2">
                  <i
                    class="i-lucide-check-circle text-green-600 dark:text-green-400 text-lg"
                  />
                  <span
                    class="text-2xl font-semibold text-n-slate-12 dark:text-n-slate-12"
                    >{{ successCount }}</span
                  >
                </div>
                <p class="text-xs text-n-slate-11 dark:text-n-slate-10">
                  Enviados com sucesso
                </p>
                <div
                  class="mt-2 text-xs flex items-center gap-1"
                  :class="
                    selectedTypes.includes('success')
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-n-slate-10 dark:text-slate-500'
                  "
                >
                  <i
                    class="text-sm"
                    :class="
                      selectedTypes.includes('success')
                        ? 'i-lucide-check-square'
                        : 'i-lucide-square'
                    "
                  />
                  <span>{{
                    selectedTypes.includes('success')
                      ? 'Selecionado'
                      : 'Clique para selecionar'
                  }}</span>
                </div>
              </div>

              <!-- Failed Card (Selectable) -->
              <div
                class="rounded-lg p-4 border-2 transition-all duration-200 cursor-pointer select-none"
                :class="
                  selectedTypes.includes('failed')
                    ? 'bg-n-iris-1 dark:bg-n-iris-3/20 border-n-iris-9 dark:border-n-iris-8'
                    : 'bg-n-slate-1 dark:bg-n-slate-3 border-n-slate-3 dark:border-n-slate-6 hover:border-n-slate-5 dark:hover:border-n-slate-7'
                "
                @click="toggleSelection('failed')"
              >
                <div class="flex items-center justify-between mb-2">
                  <i
                    class="i-lucide-x-circle text-n-ruby-9 dark:text-red-400 text-lg"
                  />
                  <span
                    class="text-2xl font-semibold text-n-slate-12 dark:text-n-slate-12"
                    >{{ failureCount }}</span
                  >
                </div>
                <p class="text-xs text-n-slate-11 dark:text-n-slate-10">
                  Falharam no envio
                </p>
                <div
                  class="mt-2 text-xs flex items-center gap-1"
                  :class="
                    selectedTypes.includes('failed')
                      ? 'text-n-ruby-11 dark:text-n-ruby-10'
                      : 'text-n-slate-10 dark:text-n-slate-9'
                  "
                >
                  <i
                    class="text-sm"
                    :class="
                      selectedTypes.includes('failed')
                        ? 'i-lucide-check-square'
                        : 'i-lucide-square'
                    "
                  />
                  <span>{{
                    selectedTypes.includes('failed')
                      ? 'Selecionado'
                      : 'Clique para selecionar'
                  }}</span>
                </div>
              </div>

              <!-- Pending Card (Selectable, Selected by Default) -->
              <div
                class="rounded-lg p-4 border-2 transition-all duration-200 cursor-pointer select-none"
                :class="
                  selectedTypes.includes('pending')
                    ? 'bg-n-iris-1 dark:bg-n-iris-3/20 border-n-iris-9 dark:border-n-iris-8'
                    : 'bg-n-slate-1 dark:bg-n-slate-3 border-n-slate-3 dark:border-n-slate-6 hover:border-n-slate-5 dark:hover:border-n-slate-7'
                "
                @click="toggleSelection('pending')"
              >
                <div class="flex items-center justify-between mb-2">
                  <i
                    class="i-lucide-clock text-n-amber-9 dark:text-amber-800 text-lg"
                  />
                  <span
                    class="text-2xl font-semibold text-n-slate-12 dark:text-n-slate-12"
                    >{{ pendingCount }}</span
                  >
                </div>
                <p class="text-xs text-n-slate-11 dark:text-n-slate-10">
                  Não processados
                </p>
                <div
                  class="mt-2 text-xs flex items-center gap-1"
                  :class="
                    selectedTypes.includes('pending')
                      ? 'text-n-amber-10 dark:text-amber-800'
                      : 'text-n-slate-10 dark:text-n-slate-9'
                  "
                >
                  <i
                    class="text-sm"
                    :class="
                      selectedTypes.includes('pending')
                        ? 'i-lucide-check-square'
                        : 'i-lucide-square'
                    "
                  />
                  <span>{{
                    selectedTypes.includes('pending')
                      ? 'Selecionado'
                      : 'Clique para selecionar'
                  }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Summary Section -->
          <div class="mb-6 p-4 bg-n-slate-1 dark:bg-n-slate-3 rounded-lg">
            <div class="flex items-center justify-between">
              <p class="text-sm text-n-slate-11 dark:text-n-slate-10">
                Total selecionado:
              </p>
              <p
                class="text-lg font-semibold text-n-slate-12 dark:text-n-slate-12"
              >
                {{ selectedCount }}
                {{ selectedCount === 1 ? 'contato' : 'contatos' }}
              </p>
            </div>
          </div>

          <!-- Warning for Including Success -->
          <div
            v-if="selectedTypes.includes('success')"
            class="mb-6 p-4 bg-n-amber-1 dark:bg-n-amber-3/20 rounded-lg border border-n-amber-4 dark:border-n-amber-7"
          >
            <div class="flex items-start gap-3">
              <i
                class="i-lucide-alert-triangle text-n-amber-9 dark:text-n-amber-10 text-lg mt-0.5"
              />
              <div>
                <p
                  class="font-semibold text-n-amber-11 dark:text-n-amber-10 mb-1"
                >
                  Atenção: Reenvio para contatos já processados
                </p>
                <p class="text-sm text-n-amber-11 dark:text-n-amber-10">
                  Você selecionou {{ successCount }} contatos que já receberam a
                  mensagem com sucesso.
                </p>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="flex gap-3">
            <button
              class="flex-1 px-4 py-3 bg-n-slate-2 hover:bg-n-slate-3 dark:bg-n-slate-4 dark:hover:bg-n-slate-5 text-n-slate-12 dark:text-n-slate-12 rounded-lg font-medium transition-colors duration-200"
              @click="$emit('close')"
            >
              Cancelar
            </button>
            <button
              :disabled="selectedCount === 0"
              class="flex-1 px-4 py-3 bg-n-iris-9 hover:bg-n-iris-10 dark:bg-n-iris-9 dark:hover:bg-n-iris-10 text-white rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              @click="handleProceed"
            >
              <i class="i-lucide-send" />
              <span
                >Reenviar para {{ selectedCount }}
                {{ selectedCount === 1 ? 'contato' : 'contatos' }}</span
              >
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
